import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { administrationApiSdk } from '@hds/administration-client-sdk';
import { haversineMiles } from '../sdk/geo.js';
import { idOf } from '../sdk/identity.js';

/**
 * The auction houses — the places sales run, read from the administration server,
 * which is the one source of truth for them.
 *
 * The app's pages ask this store a few questions (a house by `code`, its distance
 * from the dealer, the picker options), so the administration record is reshaped
 * here into the small house those questions are about:
 *
 *   id           the house's Mongo `_id`. The durable key: a followed house is
 *                saved under it, because a slug is an editable handle and a
 *                dealer's follow must not move when somebody renumbers a site.
 *   code         the house's `slug`, which is what an item carries in AUCTIONID
 *                and what a sale names in auctionHouseCode — the join in both
 *                directions. A join, never a saved reference.
 *   name         the house's name.
 *   city, state  `location.city` and `location.region`, null for a digital house
 *                that has no location.
 *   coordinates  `{ latitude, longitude }`, from the GeoJSON point `location.geo`
 *                (stored longitude first). Null when the house was never
 *                geocoded.
 *
 * Deliberately its own store rather than a slice of sales-catalog: Explore's
 * radius filter needs the roster, and sales-catalog reaches the bidding store,
 * so folding it in there would drag the whole Live Bidding surface into a
 * catalog search.
 */

// A page is as large as the server allows a request to ask for.
const PAGE_SIZE = 100;

/** An administration auction house, as the small house the app's pages read. */
export const toHouse = (document) => {
    const point = document.location?.geo?.coordinates;
    return {
        id: idOf(document),
        code: document.slug,
        name: document.name,
        city: document.location?.city ?? null,
        state: document.location?.region ?? null,
        coordinates: Array.isArray(point) && point.length === 2
            ? { latitude: point[1], longitude: point[0] }
            : null
    };
};

// Every active house, page after page, until the server's own count is reached.
async function readAllHouses() {
    const documents = [];
    for (let page = 1; ; page += 1) {
        const result = await administrationApiSdk.auctionHouses.list({ page, pageSize: PAGE_SIZE });
        const received = result?.documents ?? [];
        documents.push(...received);
        if (!received.length || documents.length >= (result?.meta?.totalCount ?? 0)) return documents;
    }
}

export const auctionHousesStore = defineStore('auctionHousesStore', () => {
    const houses = ref([]);
    const loaded = ref(false);
    const loading = ref(false);

    // code -> house. `code` is what an item carries in AUCTIONID and what a sale
    // names in auctionHouseCode, so it is the join in both directions.
    const byCode = computed(() => new Map(houses.value.map((house) => [house.code, house])));

    // Picker options for the Filter drawer's auction list, per the
    // SelectionListScreen contract. Sorted by name so the list reads
    // alphabetically rather than in insertion order.
    const options = computed(() =>
        [...houses.value]
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
            .map((house) => ({ value: house.code, label: house.name }))
    );

    /**
     * Load once per session. Never throws: a failed load leaves the roster empty,
     * which degrades to every listing being un-placeable — and an un-placeable
     * listing passes the radius filter rather than being hidden, so the feed
     * stays full instead of going silently empty.
     */
    const ensureLoaded = async () => {
        if (loaded.value || loading.value) return;
        loading.value = true;
        try {
            houses.value = (await readAllHouses()).map(toHouse);
            loaded.value = true;
        } catch (err) {
            console.warn('Auction house load failed — distances will be unresolved', err);
        } finally {
            loading.value = false;
        }
    };

    const houseFor = (code) => (code ? byCode.value.get(String(code)) ?? null : null);

    /**
     * Distance in miles from `origin` to the house a listing is at, or Infinity
     * when the house is unknown or carries no coordinates. Infinity is the
     * caller's signal to pass the listing through, not to drop it.
     */
    const distanceFrom = (code, origin) => {
        const coordinates = houseFor(code)?.coordinates;
        if (!coordinates) return Infinity;
        return haversineMiles(origin, {
            lat: coordinates.latitude,
            lng: coordinates.longitude
        });
    };

    return {
        houses,
        loaded,
        loading,
        byCode,
        options,
        ensureLoaded,
        houseFor,
        distanceFrom
    };
});

export default auctionHousesStore;
